<template>
    <div class="rm-mobile-card-container">
        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
            <i class="pi pi-spin pi-spinner" style="font-size: 3rem"></i>
            <span>{{ loadingMessage }}</span>
        </div>

        <!-- Cards -->
        <div v-else-if="!loading && tableData.length > 0" class="cards-wrapper">
            <div v-for="(item, index) in tableData" :key="item.id || index" class="rm-card">
                <div class="card-header">
                    <h3 class="card-title">{{ getFieldValue(item, 'rmCode') || 'N/A' }}</h3>
                    <div class="card-actions">
                        <Button 
                            type="button" 
                            icon="pi pi-ellipsis-v" 
                            @click="(event) => showMenu(event, item, index)" 
                            aria-haspopup="true"
                            :aria-controls="`overlay_menu_${index}`"
                            text
                            rounded />
                        <Menu :ref="`menu_${index}`" :id="`overlay_menu_${index}`" :model="menuItems" :popup="true" />
                    </div>
                </div>
                <div class="card-body">
                    <div v-for="col in validColumns" :key="col.field" class="card-row">
                        <span class="card-label">{{ col.header || col.field }}:</span>
                        <span class="card-value">
                            <Tag v-if="col.field === 'active'"
                                :value="item[col.field] === 1 || item[col.field] === true ? 'Active' : 'Inactive'"
                                :severity="(item[col.field] === 1 || item[col.field] === true) ? 'success' : 'secondary'" />
                            <span v-else-if="isDateField(col.field)">
                                {{ formatDate(item[col.field]) || '--' }}
                            </span>
                            <span v-else>
                                {{ item[col.field] || '--' }}
                            </span>
                        </span>
                    </div>
                    <div v-if="validColumns.length === 0" class="card-row">
                        <span class="card-label">No columns available</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- No Data -->
        <div v-else-if="!loading && tableData.length === 0" class="no-data-container">
            <p class="text-center">No data available</p>
        </div>

        <!-- Pagination -->
        <div v-if="!loading && tableData.length > 0" class="pagination-container">
            <Paginator 
                :rows="rows" 
                :totalRecords="totalRecords" 
                :first="first" 
                :rowsPerPageOptions="[5, 10, 20, 50]"
                @page="onPage"
                template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate="{first} to {last} of {totalRecords}">
            </Paginator>
        </div>
    </div>
</template>

<script>
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Paginator from 'primevue/paginator';
import Menu from 'primevue/menu';

export default {
    name: 'RMMobileCardView',
    components: {
        Button,
        Tag,
        Paginator,
        Menu
    },
    props: {
        tableData: {
            type: Array,
            default: () => []
        },
        defaultColumns: {
            type: Array,
            required: true
        },
        totalRecords: {
            type: Number,
            default: 0
        },
        rows: {
            type: Number,
            default: 20
        },
        first: {
            type: Number,
            default: 0
        },
        loading: {
            type: Boolean,
            default: false
        },
        loadingMessage: {
            type: String,
            default: 'Loading RM Management Data. Please wait.'
        }
    },
    data() {
        return {
            menuItems: [],
            menu: null
        };
    },
    computed: {
        validColumns() {
            // Filter out any undefined or invalid column objects and exclude 'active' field
            if (!this.defaultColumns || !Array.isArray(this.defaultColumns)) {
                return [];
            }
            return this.defaultColumns.filter(col => {
                // Only include columns that have a field and are not 'active' (since active is shown in header)
                return col && col.field && col.field !== 'active';
            });
        }
    },
    methods: {
        getFieldValue(item, field) {
            return item[field] || '--';
        },
        shouldShowField(field) {
            // This is now handled in validColumns computed property, but keeping for backward compatibility
            if (!field) return false;
            return field !== 'active';
        },
        isDateField(field) {
            return ['startDate', 'endDate', 'createdDate', 'modifiedDate', 'created_at', 'transaction_date', 'journey_created_date', 'proposal_updated_at'].includes(field);
        },
        formatDate(date) {
            if (!date) {
                return '--';
            }
            const d = new Date(date);
            const day = String(d.getDate()).padStart(2, '0');
            const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
            const month = monthNames[d.getMonth()];
            const year = d.getFullYear();
            return `${day}-${month}-${year}`;
        },
        showMenu(event, rowData, index) {
            // In mobile view, rowData is already the actual row data (not nested)
            const isActive = rowData.active === 1 || rowData.active === true;
            this.menuItems = [
                {
                    label: isActive ? 'Inactive' : 'Active',
                    icon: isActive ? 'pi pi-ban' : 'pi pi-check',
                    command: () => {
                        this.$emit('inactive-action', rowData);
                    }
                },
                {
                    label: 'Delete',
                    icon: 'pi pi-trash',
                    command: () => {
                        this.$emit('delete-action', rowData);
                    }
                }
            ];
            // Get the menu ref using the index
            const menuRef = this.$refs[`menu_${index}`];
            if (menuRef && menuRef.toggle) {
                menuRef.toggle(event);
            } else if (Array.isArray(menuRef) && menuRef[0] && menuRef[0].toggle) {
                // Fallback if ref is an array
                menuRef[0].toggle(event);
            }
        },
        onPage(event) {
            this.$emit('page-change', {
                page: event.page,
                rows: event.rows,
                first: event.first
            });
        }
    },
    mounted() {
        this.menu = this.$refs.menu;
    }
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/distributorVariables/variable.scss';

.rm-mobile-card-container {
    width: 100%;
    padding: 1rem;
}

.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    gap: 1rem;

    span {
        font-size: 1rem;
        color: #666;
    }
}

.cards-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.rm-card {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 1rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e0e0e0;
}

.card-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #333;
}

.card-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.card-body {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    min-height: 50px; /* Ensure card body has minimum height */
}

.card-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0.5rem 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
        border-bottom: none;
    }
}

.card-label {
    font-weight: 600;
    color: #666;
    font-size: 0.9rem;
    min-width: 40%;
    flex-shrink: 0;
}

.card-value {
    color: #333;
    font-size: 0.9rem;
    text-align: right;
    flex: 1;
    word-break: break-word;
}

.no-data-container {
    padding: 3rem;
    text-align: center;
    color: #666;
}

.pagination-container {
    margin-top: 1.5rem;
    display: flex;
    justify-content: center;
}

// Mobile responsive adjustments
@media (max-width: 480px) {
    .rm-mobile-card-container {
        padding: 0.5rem;
    }

    .rm-card {
        padding: 0.75rem;
    }

    .card-title {
        font-size: 1.1rem;
    }

    .card-label {
        min-width: 45%;
        font-size: 0.85rem;
    }

    .card-value {
        font-size: 0.85rem;
    }
}
</style>

